import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { currencyFormat, dateFormatter, orderTypeFormatter } from '../../../utils/formatHelper';
import StatusBadge from '../../common/StatusBadge';
import { getOrderLinePrices, getTypesBySupplierIds } from './utils';

import styles from './OrderSummaryModal.module.scss';

export function OrderSummaryInfo(props) {
  const { additionalOrderStates } = props;

  const {
    retailer: { order_type: retailerOrderType },
    retailerSuppliers,
  } = useSelector((state) => state.role);
  const { current: order } = useSelector((state) => state.retailerOrders.order);
  const { created_at, retailer_order_id, type, store, sales_order_number } = order;

  const trackingNumbers = new Set();
  let totalShipping = 0;
  let totalValue = 0;
  let totalQuantity = 0;
  let totalCommission = 0;
  let currency;

  const typesBySupplierId = getTypesBySupplierIds({
    retailerSuppliers,
    retailerOrderType: retailerOrderType || type,
  });

  order.fulfillments?.forEach((fulfillment) => {
    const { fulfillment_lines: lines, fulfillment_shipment: shipments, supplier_id: supplierId } = fulfillment;
    const types = typesBySupplierId[supplierId];

    lines.forEach((fulfillmentLine) => {
      const { quantity, shipping_price: shippingPrice, handling_price: handlingPrice } = fulfillmentLine;

      const orderLine = order.order_lines.find((orderLine) => orderLine.variant_id === fulfillmentLine.variant_id);
      const { commission, retailPrice } = getOrderLinePrices(orderLine, types);

      totalCommission += isNaN(commission) ? 0 : commission;
      totalShipping += (shippingPrice + handlingPrice) * quantity;
      totalValue += (shippingPrice + (isNaN(retailPrice) ? 0 : retailPrice) + handlingPrice) * quantity;
      totalQuantity += quantity;

      currency ||= fulfillmentLine.variant.currency;
    });

    if (fulfillment.tracking_number) {
      fulfillment.tracking_number.split(',').forEach((trackingNumber) => trackingNumbers.add(trackingNumber));
    }

    shipments?.forEach((shipment) => {
      if (shipment.tracking_number) {
        trackingNumbers.add(shipment.tracking_number);
      }
    });
  });

  const hasMoreInfo = trackingNumbers.size || additionalOrderStates.postShipmentStatus;

  return (
    <div>
      <h2>
        Order Summary <span>{retailer_order_id}</span>
      </h2>
      <hr />

      <div className={styles.info}>
        <dl>
          <dt>ID:</dt>
          <dd>{retailer_order_id}</dd>
          {sales_order_number && (
            <>
              <dt>Sales Order Number:</dt>
              <dd>{sales_order_number}</dd>
            </>
          )}
          <dt>Date:</dt>
          <dd>{dateFormatter(created_at)}</dd>
        </dl>
        <dl>
          {additionalOrderStates.displayStatus && (
            <>
              <dt>Shipment status:</dt>
              <dd>
                <StatusBadge
                  status={additionalOrderStates.displayStatus}
                  id={`retailer_order_summary_state_${order.id}`}
                  simplify
                />
              </dd>
            </>
          )}
          {additionalOrderStates.infoStatus?.length > 0 && (
            <>
              <dt>Info:</dt>
              <dd>
                <StatusBadge
                  status={additionalOrderStates.infoStatus}
                  id={`retailer_order_summary_info_state_${order.id}`}
                  simplify
                  additionalInfo
                />
              </dd>
            </>
          )}
        </dl>
        <dl>
          <dt>Type:</dt>
          <dd>{orderTypeFormatter(type)}</dd>
          <dt>Number of Units / Cases:</dt>
          <dd>{totalQuantity}</dd>
        </dl>
        <dl>
          <dt>Total Order Value:</dt>
          <dd>{currencyFormat(totalValue, currency)}</dd>
          <dt>Shipping &amp; Handling:</dt>
          <dd>{currencyFormat(totalShipping, currency)}</dd>
          {totalCommission > 0 && (
            <>
              <dt>Commission:</dt>
              <dd>{currencyFormat(totalCommission, currency)}</dd>
            </>
          )}

          {store && (
            <>
              <dt>Store:</dt>
              <dd>{store.name}</dd>
            </>
          )}
        </dl>
      </div>

      {hasMoreInfo ? (
        <>
          <hr />
          <div className={styles.list}>
            {trackingNumbers.size ? (
              <dl className={styles.tracking_number}>
                <dt>Tracking numbers:</dt>
                <dd>
                  {Array.from(trackingNumbers).map((trackingNumber) => (
                    <span key={trackingNumber}>{trackingNumber} </span>
                  ))}
                </dd>
              </dl>
            ) : null}

            {additionalOrderStates.postShipmentStatus ? (
              <dl className={styles.tracking_number}>
                <dt>Post-Shipment:</dt>
                <dd>
                  <StatusBadge
                    status={additionalOrderStates.postShipmentStatus}
                    id={`retailer_order_summary_post_shipment_state_${order.id}`}
                    simplify
                    postShipmentStatus
                  />
                </dd>
              </dl>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

OrderSummaryInfo.propTypes = {
  additionalOrderStates: PropTypes.shape({
    postShipmentStatus: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    displayStatus: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    infoStatus: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  }).isRequired,
};
